<!--
  StoreEmailTemplates.vue -- the per-type email-template editor (Phase 1 PR-4).

  The deliverable of the "Email Notifications" page redesign
  (planning/design-handoffs/handoff_2026-07-22/2026-07-22-email-notifications;
  ADR 0057). Sits BELOW the existing notification matrix + sender-identity
  card in StoreEmailConfig.vue -- it does not replace them.

  What it does:
    - EMAIL-TYPE CHOOSER (EmailTypePicker): switch across the 4 transactional
      types; each shows a Customized / Default badge.
    - HEADER IMAGE card (ImageUploader, shared): uploads set the store's
      ``email_image`` override (persisted via the campaign autosave allowlist);
      Remove clears it back to the brand image.
    - MESSAGE card (per type): a token-pill SUBJECT + BODY editor (TokenEditor)
      whose Insert-field menu is the ACTIVE TYPE'S contextual token list from
      the backend catalog -- never a hardcoded global list.
    - LIVE PREVIEW (EmailPreview, sticky): resolves every token to its sample
      value; per-type subject/CTA/detail-block/audience switch.

  Persistence:
    - Subject/body PUT to the PR-2 endpoint
      (/api/stores/<id>/email_customizations/<type>); the backend sanitizes on
      write and stores the TOKEN-KEYED form. Clearing both back to blank DELETEs
      the row (revert to default).
    - The header image + sender identity are STORE-LEVEL: header image writes
      ``campaign.email_image`` (autosave); sender identity is edited in the
      sibling card and read here reactively from the campaign prop.

  Everything is data-driven from GET .../email_customizations/_catalog so the UI
  cannot offer a token the send pipeline can't resolve.
-->
<template>
  <div class="store-email-templates" data-testid="email-templates">
    <!-- Email-type chooser row -->
    <div class="store-email-templates__chooser">
      <div class="store-email-templates__chooser-pill">
        <span class="store-email-templates__chooser-label">Editing email</span>
        <email-type-picker
          v-if="catalogLoaded"
          v-model="activeType"
          :email-types="emailTypes"
          :customized-keys="customizedKeys"
        />
      </div>
      <span class="store-email-templates__chooser-note">
        <v-icon aria-hidden="true">info</v-icon>
        Sender identity and header image apply to every email. Copy and fields
        are set per email.
      </span>
    </div>

    <div
      v-if="catalogError"
      class="store-email-templates__error"
      data-testid="email-templates-error"
    >
      <v-icon aria-hidden="true">error_outline</v-icon>
      <div class="store-email-templates__error-body">
        <p class="store-email-templates__error-title">
          Couldn't load email templates
        </p>
        <p class="store-email-templates__error-hint">
          Something went wrong loading the email settings for this store.
        </p>
        <button
          type="button"
          class="store-email-templates__retry"
          data-testid="email-templates-retry"
          @click="onRetryCatalog"
        >
          Try again
        </button>
      </div>
    </div>

    <div
      v-else-if="!catalogLoaded"
      class="store-email-templates__loading"
      data-testid="email-templates-loading"
    >
      Loading email templates&hellip;
    </div>

    <div v-else class="store-email-templates__grid">
      <!-- LEFT: editor -->
      <div class="store-email-templates__editor">
        <editor-card
          title="Header image"
          description="An optional banner across the very top of every email -- a logo lockup or seasonal art. Shared across every email."
        >
          <image-uploader
            testid-prefix="email-header-image"
            :current-image="headerImageUrl"
            :is-default="headerImageIsDefault"
            default-label="Using your brand image. Upload one to use a different banner in emails."
            :disabled="disableFields"
            :uploading="headerUploading"
            title="Add a header image"
            hint="Shown as a banner across the top of every email. PNG or JPG, 1200x360px works best."
            @upload="onHeaderUpload"
            @remove="onHeaderRemove"
            @error="onUploadError"
          />
        </editor-card>

        <editor-card :title="messageCardTitle" :description="messageCardDescription">
          <div
            v-if="activeMeta && !activeMeta.send_wired"
            class="store-email-templates__notsent"
            :data-testid="`email-not-sent-note-${activeType}`"
          >
            <v-icon aria-hidden="true">schedule</v-icon>
            <span
              >This email isn't sent automatically yet -- your copy is saved and
              will be used once sending is enabled.</span
            >
          </div>

          <label class="store-email-templates__label" :for="subjectId"
            >Subject line</label
          >
          <token-editor
            :id="subjectId"
            ref="subjectEditor"
            variant="subject"
            testid-prefix="email-subject"
            :preset-key="presetKey"
            :initial-html="subjectSeed"
            :tokens="activeTokens"
            :scope-label="activeMeta ? activeMeta.label : ''"
            :disabled="disableFields"
            aria-label="Email subject line"
            placeholder="Subject line shoppers see in their inbox..."
            @change="onSubjectChange"
            @seed="onSubjectSeed"
          />

          <label class="store-email-templates__label" :for="bodyId">Body</label>
          <token-editor
            :id="bodyId"
            ref="bodyEditor"
            variant="body"
            testid-prefix="email-body"
            :preset-key="presetKey"
            :initial-html="bodySeed"
            :tokens="activeTokens"
            :scope-label="activeMeta ? activeMeta.label : ''"
            :disabled="disableFields"
            aria-label="Email body"
            placeholder="Write the message shoppers read... Use Insert field to drop in personalized details."
            @change="onBodyChange"
            @seed="onBodySeed"
          />

          <div class="store-email-templates__pill-note">
            <v-icon aria-hidden="true">info</v-icon>
            <span>
              Fields show as blue pills like
              <span class="store-email-templates__inline-tok">First name</span>
              while you edit -- never raw
              <code>{first_name}</code>. The
              {{ activeMeta ? activeMeta.detail_label : "detail block" }},
              button and footer are added automatically.
            </span>
          </div>

          <div class="store-email-templates__actions">
            <button
              type="button"
              class="store-email-templates__reset"
              :disabled="disableFields || !isCustomized(activeType)"
              data-testid="email-reset-default"
              @click="onResetToDefault"
            >
              Reset to default copy
            </button>
            <span
              v-if="saveState"
              class="store-email-templates__savestate"
              :data-testid="`email-save-state-${activeType}`"
              >{{ saveState }}</span
            >
          </div>
        </editor-card>
      </div>

      <!-- RIGHT: live preview (sticky) -->
      <div class="store-email-templates__preview-col">
        <div class="store-email-templates__preview-head">
          <span class="store-email-templates__preview-title">
            <v-icon aria-hidden="true">visibility</v-icon>Live preview
          </span>
          <span class="store-email-templates__preview-note">Updates as you type</span>
        </div>
        <email-preview
          :type-obj="activeMeta"
          :subject-html="draftSubject"
          :body-html="draftBody"
          :from-name="campaign && campaign.notification_from_name"
          :store-name="(campaign && campaign.name) || ''"
          :store-url="storefrontUrl"
          :use-brand="!!(campaign && campaign.notification_branded_email)"
          :header-image="headerImageUrl"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import EditorCard from "./components/EditorCard.vue";
import ImageUploader from "@/components/ImageUploader.vue";
import EmailTypePicker from "./components/email/EmailTypePicker.vue";
import TokenEditor from "./components/email/TokenEditor.vue";
import EmailPreview from "./components/email/EmailPreview.vue";
import { templateToChips } from "./components/email/tokenHtml";
import { getImageBaseUrl } from "@/utils/imageBaseUrl";

// Per-type friendly name for the "detail block" mentioned in the pill note.
const DETAIL_LABELS = {
  order: "order summary",
  tracking: "tracking details",
  refund: "refund summary",
  closing: "closing summary",
};

export default {
  name: "StoreEmailTemplates",

  components: {
    EditorCard,
    ImageUploader,
    EmailTypePicker,
    TokenEditor,
    EmailPreview,
  },

  props: {
    campaign: { type: Object, required: true },
    disableFields: { type: Boolean, default: false },
  },

  emits: ["campaign-changed"],

  data() {
    return {
      catalog: [],
      catalogLoaded: false,
      // Set true when the catalog fetch fails, so the template lands in an
      // explicit error state instead of the perpetual "Loading..." spinner
      // (loading / error / loaded are mutually exclusive).
      catalogError: false,
      activeType: "order_confirmation",
      // per-type stored (customized) rows: key -> { subject, body, customized }
      customizations: {},
      // live editor drafts (token-keyed HTML): key -> { subject, body }
      drafts: {},
      // presetKey bumps to re-seed the editors when the active type changes.
      presetKey: 0,
      headerUploading: false,
      saveState: "",
      _saveTimer: null,
    };
  },

  computed: {
    storeId() {
      return this.campaign?._jv?.id || this.campaign?.id || null;
    },
    emailTypes() {
      return this.catalog;
    },
    /**
     * The real storefront URL for the preview's {store_url} token, built the
     * same way the rest of the editor builds it (StoreShopper.shopPrefix) and
     * the same way the backend's ``frontend_url("/shop/<slug>")`` does -- a
     * path on the app host, NOT the ``<slug>.threadflow.store`` subdomain the
     * token catalog's sample implies. Empty until the store has a slug, which
     * falls back to the catalog sample rather than emitting a broken URL.
     */
    storefrontUrl() {
      const slug = this.campaign && this.campaign.slug;
      if (!slug) return "";
      // BASE_URL is the build's PUBLIC PATH, which is "/" in most builds --
      // useful as a prefix but NOT a host. An email URL has to be absolute,
      // so only honour BASE_URL when it actually carries a scheme; otherwise
      // fall back to the current origin.
      const configured = process.env.BASE_URL || "";
      const base = (
        /^https?:\/\//i.test(configured) ? configured : window.location.origin || ""
      ).replace(/\/+$/, "");
      if (!base) return "";
      return `${base}/shop/${String(slug).toLowerCase()}`;
    },
    activeMeta() {
      const found = this.catalog.find((t) => t.key === this.activeType) || null;
      if (!found) return null;
      return {
        ...found,
        detail_label: DETAIL_LABELS[found.detail] || "detail block",
      };
    },
    activeTokens() {
      return (this.activeMeta && this.activeMeta.tokens) || [];
    },
    customizedKeys() {
      return Object.keys(this.customizations).filter((k) =>
        this.isCustomized(k),
      );
    },
    // Seed HTML for the editors when the active type (presetKey) changes.
    subjectSeed() {
      const row = this.customizations[this.activeType];
      if (row && row.customized && row.subject) return row.subject;
      // Fall back to the type default rendered as chips so the editor shows
      // the starting copy (matches the design's tplToChips seeding).
      return this.defaultSubjectChips(this.activeType);
    },
    bodySeed() {
      const row = this.customizations[this.activeType];
      if (row && row.customized && row.body) return row.body;
      return this.defaultBodyChips(this.activeType);
    },
    draftSubject() {
      const d = this.drafts[this.activeType];
      return d ? d.subject : "";
    },
    draftBody() {
      const d = this.drafts[this.activeType];
      return d ? d.body : "";
    },
    subjectId() {
      return `email-subject-${this.activeType}`;
    },
    bodyId() {
      return `email-body-${this.activeType}`;
    },
    messageCardTitle() {
      return `Message${this.activeMeta ? " - " + this.activeMeta.label : ""}`;
    },
    messageCardDescription() {
      return "Your custom copy for this email. Insert personalized fields anywhere -- they read as friendly names here and resolve to each recipient's real details when the email sends.";
    },
    // Effective header image URL (email_image override else brand_image).
    headerImageUrl() {
      const key =
        (this.campaign && (this.campaign.email_image || this.campaign.brand_image)) ||
        null;
      if (!key || String(key).includes("://")) return null;
      return `${getImageBaseUrl()}${key}`;
    },
    // True when the preview above is the INHERITED brand image (no email_image
    // override set), not an owned override. Drives the uploader's "default"
    // state: no Remove button (nothing to remove -- the override is already
    // empty), a "using your brand image" caption, and an Upload action to SET
    // an override. Without this, clearing the override fell back to the brand
    // image in the normal filled+Remove state, so Remove looked broken.
    headerImageIsDefault() {
      const c = this.campaign || {};
      const override = (c.email_image || "").trim();
      const brand = (c.brand_image || "").trim();
      return !override && !!brand;
    },
  },

  watch: {
    activeType() {
      // Re-seed the editors from the newly-active type's stored/default copy.
      this.presetKey += 1;
      this.saveState = "";
    },
  },

  mounted() {
    this.loadCatalog();
  },

  methods: {
    async loadCatalog() {
      if (!this.storeId || !this.$axios) {
        // No store id yet (brand-new store pre-persist): render nothing until
        // the id lands. The parent re-mounts on id capture.
        return;
      }
      this.catalogError = false;
      try {
        const resp = await this.$axios.get(
          `/stores/${this.storeId}/email_customizations/_catalog`,
        );
        this.catalog = (resp?.data?.email_types) || [];
        this.catalogLoaded = true;
        // Load each type's stored customization so badges + seeds are correct.
        await this.loadAllCustomizations();
      } catch (err) {
        // The fetch failed (e.g. a correct 404 for a caller who does not own
        // this store). Land in an explicit error state rather than leaving the
        // perpetual "Loading..." spinner up forever (card Gu0qltbI).
        this.catalogError = true;
        // eslint-disable-next-line no-console
        console.warn("Failed to load email catalog", describeError(err));
      }
    },

    onRetryCatalog() {
      this.loadCatalog();
    },

    async loadAllCustomizations() {
      const results = await Promise.all(
        this.catalog.map((t) => this.fetchCustomization(t.key)),
      );
      const map = {};
      const drafts = {};
      this.catalog.forEach((t, i) => {
        const row = results[i] || { customized: false, subject: "", body: "" };
        map[t.key] = row;
        // Seed the live draft from stored copy or the default chips.
        drafts[t.key] = {
          subject:
            row.customized && row.subject
              ? row.subject
              : this.defaultSubjectChips(t.key),
          body:
            row.customized && row.body
              ? row.body
              : this.defaultBodyChips(t.key),
        };
      });
      this.customizations = map;
      this.drafts = drafts;
      // Force initial seed of the editors now that data is present.
      this.presetKey += 1;
    },

    async fetchCustomization(key) {
      try {
        const resp = await this.$axios.get(
          `/stores/${this.storeId}/email_customizations/${key}`,
        );
        const b = resp?.data || {};
        return {
          customized: !!b.customized,
          subject: b.subject || "",
          body: b.body || "",
        };
      } catch (err) {
        return { customized: false, subject: "", body: "" };
      }
    },

    isCustomized(key) {
      const row = this.customizations[key];
      return !!(row && row.customized);
    },

    // Build chip HTML from a type's token-keyed default subject/body so the
    // editor opens showing the starting copy.
    defaultSubjectChips(key) {
      const meta = this.catalog.find((t) => t.key === key);
      if (!meta) return "";
      return templateToChips(meta.default_subject || "", (k) =>
        this.labelOfType(key, k),
      );
    },
    defaultBodyChips(key) {
      const meta = this.catalog.find((t) => t.key === key);
      if (!meta) return "";
      return templateToChips(meta.default_body || "", (k) =>
        this.labelOfType(key, k),
      );
    },
    labelOfType(typeKey, tokenKey) {
      const meta = this.catalog.find((t) => t.key === typeKey);
      if (!meta) return undefined;
      const tok = (meta.tokens || []).find((t) => t.key === tokenKey);
      return tok ? tok.label : undefined;
    },

    onSubjectChange(html) {
      this.ensureDraft(this.activeType).subject = html;
      this.queueSave();
    },
    onBodyChange(html) {
      this.ensureDraft(this.activeType).body = html;
      this.queueSave();
    },
    // Programmatic (re)seed of an editor -> sync the draft for the live
    // preview but do NOT queue a save. Viewing a type must never write to
    // the server; only a real user edit (@change) persists.
    onSubjectSeed(html) {
      this.ensureDraft(this.activeType).subject = html;
    },
    onBodySeed(html) {
      this.ensureDraft(this.activeType).body = html;
    },
    ensureDraft(key) {
      if (!this.drafts[key]) {
        this.drafts = { ...this.drafts, [key]: { subject: "", body: "" } };
      }
      return this.drafts[key];
    },

    // Debounced persist of the active type's draft to the PR-2 endpoint.
    queueSave() {
      if (this.disableFields) return;
      this.saveState = "Saving...";
      if (this._saveTimer) clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => this.saveActive(), 600);
    },

    // Determine whether the draft equals the type's default (chip form). If
    // both fields are blank OR both match the default, we DELETE the row
    // (revert to default) rather than persist a redundant customization.
    isDraftDefault(key) {
      const d = this.drafts[key] || { subject: "", body: "" };
      const defSubj = this.defaultSubjectChips(key);
      const defBody = this.defaultBodyChips(key);
      const norm = (s) => (s || "").replace(/\s+/g, " ").trim();
      const subjEmpty = norm(d.subject) === "";
      const bodyEmpty = norm(d.body) === "";
      if (subjEmpty && bodyEmpty) return true;
      return norm(d.subject) === norm(defSubj) && norm(d.body) === norm(defBody);
    },

    async saveActive() {
      const key = this.activeType;
      if (!this.storeId || !this.$axios) return;
      const d = this.drafts[key] || { subject: "", body: "" };
      try {
        if (this.isDraftDefault(key)) {
          // Revert to default: DELETE the sparse row (idempotent).
          await this.$axios.delete(
            `/stores/${this.storeId}/email_customizations/${key}`,
          );
          this.customizations = {
            ...this.customizations,
            [key]: { customized: false, subject: "", body: "" },
          };
        } else {
          const resp = await this.$axios.put(
            `/stores/${this.storeId}/email_customizations/${key}`,
            { subject: d.subject, body: d.body },
          );
          const b = resp?.data || {};
          this.customizations = {
            ...this.customizations,
            [key]: {
              customized: !!b.customized,
              subject: b.subject || "",
              body: b.body || "",
            },
          };
        }
        this.saveState = "Saved";
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to save email customization", describeError(err));
        this.saveState = "Save failed";
      }
    },

    async onResetToDefault() {
      const key = this.activeType;
      // Clear the drafts to the default chips and DELETE the row.
      this.drafts = {
        ...this.drafts,
        [key]: {
          subject: this.defaultSubjectChips(key),
          body: this.defaultBodyChips(key),
        },
      };
      this.presetKey += 1;
      if (!this.storeId || !this.$axios) return;
      try {
        await this.$axios.delete(
          `/stores/${this.storeId}/email_customizations/${key}`,
        );
        this.customizations = {
          ...this.customizations,
          [key]: { customized: false, subject: "", body: "" },
        };
        this.saveState = "Reset to default";
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to reset email customization", describeError(err));
      }
    },

    // --- Header image (store-level email_image override) ---
    async onHeaderUpload(file) {
      if (!this.$axios || this.disableFields) return;
      this.headerUploading = true;
      const form = new FormData();
      form.append("file", file);
      try {
        const resp = await this.$axios.post(`/upload_image`, form, {
        });
        const uuid = resp?.data?.uuid;
        if (uuid) {
          // Store-level override -> persisted through the campaign autosave
          // allowlist (email_image). Mutate in place like the sibling cards.
          this.campaign.email_image = uuid;
          this.$emit("campaign-changed");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to upload email header image", describeError(err));
      } finally {
        this.headerUploading = false;
      }
    },

    onHeaderRemove() {
      if (this.disableFields) return;
      // Clear the override; effective image falls back to brand_image.
      this.campaign.email_image = "";
      this.$emit("campaign-changed");
    },

    onUploadError(message) {
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch("raiseError", message);
      }
    },
  },

  beforeUnmount() {
    if (this._saveTimer) clearTimeout(this._saveTimer);
  },
};
</script>

<style scoped>
.store-email-templates__chooser {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.store-email-templates__chooser-pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid var(--tf-border, #cfd8e3);
  border-radius: 9999px;
  padding: 6px 6px 6px 16px;
}
.store-email-templates__chooser-label {
  font-size: 12px;
  color: var(--tf-fg-4, #78909c);
}
.store-email-templates__chooser-note {
  font-size: 12px;
  color: var(--tf-fg-4, #78909c);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.store-email-templates__loading {
  padding: 24px;
  font-size: 13px;
  color: var(--tf-fg-4, #78909c);
}
.store-email-templates__error {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 20px 24px;
  border: 1px solid var(--tf-border-2, #e0e0e0);
  border-radius: 8px;
  background: var(--tf-bg-2, #fafafa);
  color: var(--tf-fg-2, #37474f);
}
.store-email-templates__error .v-icon,
.store-email-templates__error i {
  color: var(--tf-danger, #c62828);
}
.store-email-templates__error-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
}
.store-email-templates__error-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--tf-fg-4, #78909c);
}
.store-email-templates__retry {
  font-size: 13px;
  font-weight: 600;
  color: var(--tf-accent, #1565c0);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.store-email-templates__retry:hover {
  text-decoration: underline;
}
.store-email-templates__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 430px;
  gap: 20px;
  align-items: start;
}
.store-email-templates__preview-col {
  position: sticky;
  /* Below the editor's sticky pager row (StoreEditShell sets --ces-pager-h). */
  top: var(--ces-pager-h, 0px);
}
.store-email-templates__preview-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.store-email-templates__preview-title {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: "Poppins", "Roboto", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--tf-fg-1, #133752);
}
.store-email-templates__preview-note {
  font-size: 11px;
  color: var(--tf-fg-4, #78909c);
}
.store-email-templates__label {
  display: block;
  font-size: 12px;
  color: var(--tf-primary, #119ae9);
  margin: 18px 0 8px;
}
.store-email-templates__label:first-of-type {
  margin-top: 4px;
}
.store-email-templates__notsent {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--tf-warn-soft, #fff8e1);
  color: var(--tf-warn-fg, #8d6e00);
  font-size: 12.5px;
  line-height: 1.5;
}
.store-email-templates__pill-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  font-size: 12px;
  color: var(--tf-fg-4, #78909c);
  line-height: 1.5;
}
.store-email-templates__inline-tok {
  display: inline-block;
  font-size: 11px;
  color: var(--tf-token-fg, #2456b8);
  background: var(--tf-token-bg, #eef4ff);
  border: 1px solid var(--tf-token-border, #cfe0fb);
  border-radius: 5px;
  padding: 0 5px;
}
.store-email-templates__pill-note code {
  font-family: ui-monospace, monospace;
  font-size: 11.5px;
  background: #f3f5f8;
  padding: 1px 5px;
  border-radius: 3px;
}
.store-email-templates__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}
.store-email-templates__reset {
  border: 1px solid var(--tf-border, #cfd8e3);
  border-radius: 8px;
  background: #fff;
  color: var(--tf-fg-3, #546e7a);
  font-size: 12.5px;
  padding: 6px 12px;
  cursor: pointer;
}
.store-email-templates__reset:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.store-email-templates__savestate {
  font-size: 12px;
  color: var(--tf-fg-4, #78909c);
}

/* Responsive: <=1080px stack the grid + un-stick the preview (ADR 0024). */
@media (max-width: 1080px) {
  .store-email-templates__grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .store-email-templates__preview-col {
    position: static;
  }
}
</style>
